<template>
<div class="sidebar-shell" :class="{ 'pinned-open': isDesktopPinnedOpen }">
	<div class="sidebar-controls">
		<button
			ref="menuToggle"
			type="button"
			class="btn btn-dark btn-sm menu-button sidebar-toggle"
			@click.stop="handleToggleClick"
			aria-label="Кнопка меню"
		>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				width="24"
				height="24"
				fill="currentColor"
				class=""
				viewBox="0 0 16 16"
			>
				<path
					fill-rule="evenodd"
					d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5"
				/>
			</svg>
		</button>

		<button
			v-if="isDesktop && isOpen"
			type="button"
			class="btn btn-dark btn-sm sidebar-pin-button"
			@click.stop="togglePin"
			:aria-label="isPinnedOpen ? 'Відкріпити меню' : 'Закріпити меню'"
			:title="isPinnedOpen ? 'Відкріпити меню' : 'Закріпити меню'"
		>
			<svg
				v-if="!isPinnedOpen"
				xmlns="http://www.w3.org/2000/svg"
				width="20"
				height="20"
				fill="currentColor"
				class=""
				viewBox="0 0 16 16"
			>
				<path d="M9.828.722a.5.5 0 0 1 .354.146l4.95 4.95a.5.5 0 0 1 0 .707c-.48.48-1.072.588-1.503.588-.177 0-.335-.018-.46-.039l-3.134 3.134a6 6 0 0 1 .16 1.013c.046.702-.032 1.687-.72 2.375a.5.5 0 0 1-.707 0l-2.829-2.828-3.182 3.182c-.195.195-1.219.902-1.414.707s.512-1.22.707-1.414l3.182-3.182-2.828-2.829a.5.5 0 0 1 0-.707c.688-.688 1.673-.767 2.375-.72a6 6 0 0 1 1.013.16l3.134-3.133a3 3 0 0 1-.04-.461c0-.43.108-1.022.589-1.503a.5.5 0 0 1 .353-.146"/>
			</svg>

			<svg
				v-else
				xmlns="http://www.w3.org/2000/svg"
				width="20"
				height="20"
				fill="currentColor"
				class=""
				viewBox="0 0 18 18"
			>
				<path d="M4.146.146A.5.5 0 0 1 4.5 0h7a.5.5 0 0 1 .5.5c0 .68-.342 1.174-.646 1.479-.126.125-.25.224-.354.298v4.431l.078.048c.203.127.476.314.751.555C12.36 7.775 13 8.527 13 9.5a.5.5 0 0 1-.5.5h-4v4.5c0 .276-.224 1.5-.5 1.5s-.5-1.224-.5-1.5V10h-4a.5.5 0 0 1-.5-.5c0-.973.64-1.725 1.17-2.189A6 6 0 0 1 5 6.708V2.277a3 3 0 0 1-.354-.298C4.342 1.674 4 1.179 4 .5a.5.5 0 0 1 .146-.354"/>
			</svg>
		</button>
	</div>

	<nav
		ref="menuContainer"
		class="navbar d-md-block bg-light sidebar navbar-expand-lg py-1"
		:class="{
			'open px-3': displayMode === 'open',
			minimized: displayMode === 'minimized',
			'pinned-open': isDesktopPinnedOpen,
		}"
		@click.stop
	>
		<div class="position-sticky pb-0 mb-0 sidebar-sticky w-100 h-100">
			<div class="nav navbar-nav d-flex flex-column align-items-start flex-nowrap h-100" >
				<main-menu
					:display-mode="displayMode"
					@signout="$emit('signout')"
					@expand="openMenu"
				/>
			</div>
		</div>
	</nav>

	<div
		v-if="isOpen && !isDesktopPinnedOpen"
		class="sidebar-backdrop"
		@click="closeMenu"
	/>
</div>
</template>


<script>
import { useLocalStorage, useEventListener } from '@vueuse/core'
import { useSizeBreakpoint } from '@/composables/size-breakpoint.js'
import { useAccontStore } from '@/stores/AccountStore.js'
import MainMenu from './MainMenu.vue'


export default {
	inject: ['headerHeight'],

	components: {
		MainMenu
	},

	props: {
	},

	emits: {
		signout: null,
	},

	setup() {
		const { breakpoint, isDesktop } = useSizeBreakpoint()
		const accountStore = useAccontStore()
		const sidebarModeStore = useLocalStorage('sidebar-mode', 'mobile')

		return {
			useEventListener,
			breakpoint,
			isDesktop,
			accountStore,
			sidebarModeStore,
		}
	},

	data() {
		return {
			isOpen: false,
			isPinnedOpen: false,
		}
	},

	watch: {
		$route() {
			if (this.isDesktopPinnedOpen) {
				return
			}

			this.closeMenu()
		},
	},

	created() {
		// Parse sidebar mode
		switch (this.sidebarModeStore) {
			case 'open-pinned':
				this.isOpen = true
				this.isPinnedOpen = true
				break
			case 'open-overlay':
			case 'minimized':
				this.isOpen = false
				this.isPinnedOpen = false
				break
			case 'mobile':
				this.isOpen = false
				this.isPinnedOpen = false
				break
			case 'hidden':
				this.isOpen = false
				this.isPinnedOpen = false
				break
		}
	},

	mounted() {
		useEventListener(document, 'click', this.handleDocumentClick)
	},

	methods: {
		handleToggleClick() {
			if (this.isDesktopPinnedOpen || this.isOpen) {
				this.closeMenu()
			} else {
				this.openMenu()
			}

			this.shareSidebarMode(this.sidebarMode)
		},

		openMenu() {
			this.isOpen = true
		},

		closeMenu() {
			this.isOpen = false
			this.isPinnedOpen = false
		},

		togglePin() {
			if (!this.isDesktop || !this.isOpen) {
				return
			}

			this.isPinnedOpen = !this.isPinnedOpen

			this.shareSidebarMode(this.sidebarMode)
		},

		shareSidebarMode(mode) {
			this.sidebarModeStore = mode
		},

		handleDocumentClick(event) {
			if (!this.isOpen || this.isDesktopPinnedOpen) {
				return
			}

			const menuContainer = this.$refs.menuContainer
			const menuToggle = this.$refs.menuToggle
			const target = event.target

			if (!menuContainer || !target) {
				return
			}

			const clickedInsideMenu = menuContainer.contains(target)
			const clickedToggle = menuToggle && menuToggle.contains(target)

			if (!clickedInsideMenu && !clickedToggle) {
				this.closeMenu()
			}
		},
	},

	computed: {
		// isDesktop() {
		// 	return ['lg', 'xl', 'xxl'].includes(this.breakpoint)
		// },

		isDesktopPinnedOpen() {
			return this.isDesktop && this.isOpen && this.isPinnedOpen
		},

		sidebarMode() {
			if (!this.isDesktop) {
				return 'mobile'
			}

			if (this.isDesktopPinnedOpen) {
				return 'open-pinned'
			}

			if (this.isOpen) {
				return 'open-overlay'
			}

			return 'minimized'
		},

		displayMode() {
			if (this.isOpen) {
				return 'open'
			}

			return this.isDesktop ? 'minimized' : 'hidden'
		},
	},
}
</script>


<style scoped>
/*
* Sidebar
*/
* {
	/* Hide scroll bar, but while still being able to scroll */
	scrollbar-width: none;
}

.sidebar-shell {
	position: relative;
}

.sidebar-shell.pinned-open {
	position: fixed;
	top: v-bind(headerHeight);
	left: 0;
	bottom: 0;
	width: 14rem;
	z-index: 1030;
}

.sidebar-shell.pinned-open .sidebar {
	position: relative;
	top: 0;
	bottom: auto;
	height: 100%;
	margin-right: 0;
}

.sidebar-controls {
	position: fixed;
	top: 4px;
	left: 4px;
	z-index: 1040;
	display: inline-flex;
	gap: 0.25rem;
}

.sidebar {
	position: fixed;
	top: v-bind(headerHeight);
	bottom: 0;
	left: 0;
	z-index: 1030; /* Behind the header */
	width: 14rem;
	min-width: 5rem;
	max-width: 100%;
	font-weight: 500;
	white-space: nowrap;
	padding: 0.5rem;
	margin-right: 4px;
	background-color: #c6c7c8;
	color: inherit;

	transform: translateX(-100%);
	transition: transform 0.2s ease-in-out, width 0.2s ease-in-out;
}

/* Desktop minimized strip: narrow, icon-only, always visible */
.sidebar.minimized {
	transform: translateX(0);
	width: 45px;
	min-width: 45px;
	padding: 0.3rem;
	box-shadow: inset -1px 0 0 rgba(0, 0, 0, .1), 0 0.5rem 1rem rgba(0, 0, 0, .15);
}

/* Fully expanded menu (both desktop and mobile) */
.sidebar.open {
	transform: translateX(0);
	box-shadow: inset -1px 0 0 rgba(0, 0, 0, .1), 0 0.5rem 1rem rgba(0, 0, 0, .15);
}

.sidebar.pinned-open {
	z-index: 1030;
}

.menu-button {
	width: 2.5rem;
	height: 2.5rem;
	background-color: transparent;
	color: #c0c0c8;
	border: 0;
}

.menu-button:hover {
	color: #fff !important;
}

.sidebar-toggle {
	width: 2.5rem;
	height: 2.5rem;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0;
}

.sidebar-pin-button {
	width: 2.5rem;
	height: 2.5rem;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0;
	background-color: transparent;
	color: #c0c0c8;
	border: 0;
}

.sidebar-pin-button:hover {
	color: #fff !important;
}

.sidebar-backdrop {
	position: fixed;
	inset: 0;
	z-index: 1020;
	background-color: rgba(0, 0, 0, 0.2);
}

.sidebar-sticky {
	height: 100%;
	overflow-x: hidden;
	overflow-y: auto;
}

/* Allow hover tooltips to overflow the narrow strip in minimized mode */
.sidebar.minimized .sidebar-sticky {
	overflow: visible;
}
</style>
